// src/pages/FundPlanner/lib/solverNet.js
import dayjs from "lib/dayjs";
import {
  SLGS_DENOM_UNIT,
  SLGS_ROUND_MODE,
  tenorToMonths,
  deriveMaturity,
  buildSlgsBands,
  findSlgsBandForMaturity,
  dcfLogged,
  isSlgsCertificateMaturity,
} from "./solverMain";
import { solveWithGLPK } from "lib/glpkAdapter";
import { buildSlgsNoteCashflows } from "./slgsNoteSchedule";
import {
  buildInitialPrincipalsFromLP,
  trimNetFundingSolution,
} from "./solverTrim";

/**
 * Net funding solver (LP-based, least-cost, SLGS-aware)
 * -----------------------------------------------------
 * Objective: minimize total principal cost + cash deposit
 * while ensuring:
 *  • Non-negative escrow balance at each draw date
 *  • Final balance = 0
 *
 * LP variables:
 *  • x_k  : principal of SLGS candidate k (per $1 units, rounded later)
 *  • D    : cash deposit at settlement
 *  • B_i  : balance after period i (after deposit + flows)
 *
 * Discrete stage:
 *  • Round LP x_k → denomination grid
 *  • Run local "trimming" search (hill-climbing) to minimize
 *      C(P) = Σ P_k + D(P)
 *    where D(P) is derived from minimum balance *without* deposit.
 *
 * NEW MODEL:
 *  • No leadDays — maturity is the draw date
 *  • Period time T_i = draw.date (startOf("day"))
 */
export async function solveNetFundingStructure(
  draws = [],
  rates = [],
  _dayCount = "act/act",
  selectedCurve = "slgs",
  subscriptionDate,
  settlementDate
) {
  if (!Array.isArray(draws) || draws.length === 0) return emptyResult();
  if (!Array.isArray(rates) || rates.length === 0) return emptyResult();

  // ──────────────────────────────────────────────
  // PERF timers
  // ──────────────────────────────────────────────
  const t0All = performance.now();
  const perf = {
    totalMs: 0,
    normalizeMs: 0,
    drawsMs: 0,
    candidatesMs: 0,
    lpVarsMs: 0,
    constraintsMs: 0,
    finalizeMs: 0,
    buildModelMs: 0,
    glpkSolveMs: 0,
    discreteMs: 0,
    buildOutputMs: 0,
    meta: {},
  };

  const today = dayjs();
  const subDateStr =
    subscriptionDate || rates[0]?.asOf || today.format("YYYY-MM-DD");
  const subDate = dayjs(subDateStr);
  const settleDate = dayjs(settlementDate || subDateStr).startOf("day");
  const settleStr = settleDate.format("YYYY-MM-DD");

  // ──────────────────────────────────────────────
  // Normalize rates → usable SLGS rows
  // ──────────────────────────────────────────────
  {
    const t = performance.now();

    const normalized = rates.map((r, idx) => {
      let maturity = r.maturity;
      if (!maturity && r.tenor) maturity = deriveMaturity(settleDate, r.tenor);

      const maturityDayjs = maturity ? dayjs(maturity) : null;
      const maturityStr =
        maturityDayjs?.isValid?.() ? maturityDayjs.format("YYYY-MM-DD") : null;

      const ratePct = Number(r.value ?? r.ratePct ?? 0);

      const isSLGS =
        selectedCurve?.toLowerCase() === "slgs" ||
        r.ticker?.toUpperCase?.().startsWith?.("SLGA") ||
        r.code?.toUpperCase?.().startsWith?.("SLGA");

      const months =
        typeof r.months === "number"
          ? r.months
          : r.tenor
          ? tenorToMonths(r.tenor)
          : null;

      const type = isSLGS ? "SLGS" : "Other";

      let name;
      if (isSLGS) {
        if (maturityStr && isSlgsCertificateMaturity(maturityStr, settleDate)) {
          name = `SLGS Certificate ${r.tenor || ""}`.trim();
        } else if (maturityStr) {
          name = `SLGS Note ${r.tenor || ""}`.trim();
        } else {
          name = `SLGS Time Deposit ${r.tenor || ""}`.trim();
        }
      } else {
        name = r.name?.trim() || r.asset_class || r.security_id || "Security";
      }

      return {
        ...r,
        maturity: maturityStr,
        ratePct,
        type,
        name,
        months,
        price: 1.0,
        _rowIndex: idx,
      };
    });

    perf.normalizeMs = performance.now() - t;

    var slgsRowsSorted = normalized
      .filter((n) => n.type === "SLGS" && Number.isFinite(n.ratePct))
      .sort((a, b) => (a.months ?? 999999) - (b.months ?? 999999));
  }

  const slgsBands = buildSlgsBands(settleDate, slgsRowsSorted);

  // ──────────────────────────────────────────────
  // Ascending draws + periods
  // ──────────────────────────────────────────────
  let drawsAsc;
  let periods;

  {
    const t = performance.now();

    drawsAsc = [...draws]
      .filter((d) => d?.date && Number(d?.amount) > 0)
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

    if (drawsAsc.length === 0) return emptyResult();

    periods = drawsAsc.map((draw) => {
      const drawDate = dayjs(draw.date).startOf("day");
      const T_i = drawDate;
      const F_i = Number(draw.amount) || 0;
      return { draw, T_i, F_i };
    });

    perf.drawsMs = performance.now() - t;
  }

  // Horizon = last draw date
  const horizon = dayjs(drawsAsc[drawsAsc.length - 1].date).startOf("day");

  // ──────────────────────────────────────────────
  // Build SLGS candidate universe once for [first T_i, horizon]
  // ──────────────────────────────────────────────
  let allCandidates;
  {
    const t = performance.now();
    const firstTi = periods[0].T_i;

    allCandidates = buildCandidatesForHorizon(
      firstTi,
      horizon,
      settleDate,
      subDate,
      slgsBands
    );

    perf.candidatesMs = performance.now() - t;
  }

  if (!allCandidates.length) return emptyResult();

  // LP container
  const lp = {
    name: "fundplanner_net",
    objective: { direction: "min", name: "cost", vars: [] },
    subjectTo: [],
    bounds: [],
  };

  // ──────────────────────────────────────────────
  // LP variables
  // ──────────────────────────────────────────────
  const balanceVarNames = [];
  {
    const t = performance.now();

    // Deposit variable D
    lp.objective.vars.push({ name: "D", coef: 1 });
    lp.bounds.push({ name: "D", lb: 0, ub: 1e50 });

    // SLGS principal variables x_k
    allCandidates.forEach((cand, idx) => {
      const varName = `x_${idx}`;
      cand.varName = varName;
      lp.objective.vars.push({ name: varName, coef: 1 });
      lp.bounds.push({ name: varName, lb: 0, ub: 1e50 });
    });

    // Balance variables B_i (one per period)
    for (let i = 0; i < periods.length; i++) {
      const Bi = `B_${i + 1}`;
      balanceVarNames.push(Bi);
      lp.objective.vars.push({ name: Bi, coef: 0 });
      lp.bounds.push({ name: Bi, lb: 0, ub: 1e50 });
    }

    perf.lpVarsMs = performance.now() - t;
  }

  // ──────────────────────────────────────────────
  // Balance recursion constraints (PERF hotspot)
  // ──────────────────────────────────────────────
  {
    const t = performance.now();

    let prevT = settleDate.clone();
    periods.forEach((period, i) => {
      const idx = i + 1;
      const { T_i, F_i } = period;
      const rowName = `bal_${idx}`;
      const cols = [];

      // B_i coefficient
      const Bi = balanceVarNames[i];
      cols.push({ name: Bi, coef: 1 });

      if (idx === 1) {
        cols.push({ name: "D", coef: -1 });
      } else {
        const Bprev = balanceVarNames[i - 1];
        cols.push({ name: Bprev, coef: -1 });
      }

      // Cash in this period from each candidate (per $1 principal)
      for (const cand of allCandidates) {
        const cashInPeriod = cand.inflowsPerDollar.reduce((s, cf) => {
          const d = dayjs(cf.date);
          return d.isAfter(prevT, "day") && d.isSameOrBefore(T_i, "day")
            ? s + cf.amount
            : s;
        }, 0);
        if (cashInPeriod !== 0) {
          cols.push({ name: cand.varName, coef: -cashInPeriod });
        }
      }

      const rhs = -F_i;
      lp.subjectTo.push({
        name: rowName,
        vars: cols,
        bnds: { lb: rhs, ub: rhs },
      });

      prevT = T_i.clone();
    });

    perf.constraintsMs = performance.now() - t;
  }

  // Final balance constraint: B_n = 0
  const finalBalanceVar = balanceVarNames[balanceVarNames.length - 1];
  lp.subjectTo.push({
    name: "final_balance",
    vars: [{ name: finalBalanceVar, coef: 1 }],
    bnds: { lb: 0, ub: 0 },
  });

  // ──────────────────────────────────────────────
  // Finalize model
  // ──────────────────────────────────────────────
  {
    const t = performance.now();
    finalizeModel(lp);
    ensureBoundTypes(lp);
    summarizeModel(lp);
    perf.finalizeMs = performance.now() - t;
  }

  // ──────────────────────────────────────────────
  // Build GLPK model (PERF hotspot)
  // ──────────────────────────────────────────────
  let model;
  {
    const t = performance.now();

    model = {
      c: lp.objective.vars.map((v) => v.coef),
      rows: lp.subjectTo.map((r) => {
        const coeffs = lp.objective.vars.map((ov) => {
          const found = (r.vars || []).find((rv) => rv.name === ov.name);
          return found ? found.coef ?? 0 : 0;
        });

        const rhs = r.bnds.lb ?? 0;
        return { coeffs, sense: "=", rhs };
      }),
      lb: lp.objective.vars.map((ov) => {
        const b = lp.bounds.find((b) => b.name === ov.name);
        return b?.lb ?? 0;
      }),
      ub: lp.objective.vars.map((ov) => {
        const b = lp.bounds.find((b) => b.name === ov.name);
        return b?.ub ?? 1e50;
      }),
      N: lp.objective.vars.length,
      names: lp.objective.vars.map((v) => v.name),
    };

    perf.buildModelMs = performance.now() - t;
  }

  // ──────────────────────────────────────────────
  // Solve with GLPK
  // ──────────────────────────────────────────────
  let res;
  {
    const t = performance.now();
    res = await solveWithGLPK(model);
    perf.glpkSolveMs = performance.now() - t;
  }

  if (!res?.vars || !Object.keys(res.vars).length) {
    perf.totalMs = performance.now() - t0All;
    perf.meta = {
      mode: "net",
      periods: periods?.length ?? 0,
      candidates: allCandidates?.length ?? 0,
      vars: lp?.objective?.vars?.length ?? 0,
      rows: lp?.subjectTo?.length ?? 0,
    };
    return emptyResult();
  }

  const vars = res.vars;

  // ──────────────────────────────────────────────
  // Discrete stage: rounding + trimming
  // ──────────────────────────────────────────────
  let activeCandidates;
  let initialPrincipals;
  let trimResult;

  {
    const t = performance.now();

    activeCandidates = allCandidates.filter(
      (c) => Number(vars[c.varName] ?? 0) > 1e-6
    );

    if (!activeCandidates.length) {
      activeCandidates = allCandidates;
    }

    initialPrincipals = buildInitialPrincipalsFromLP({
      candidates: activeCandidates,
      lpVars: vars,
      denomination: SLGS_DENOM_UNIT,
      roundMode: SLGS_ROUND_MODE || "ceil",
    });

    trimResult = trimNetFundingSolution({
      candidates: activeCandidates,
      periods,
      settleDate,
      initialPrincipals,
      denomination: SLGS_DENOM_UNIT,
      maxIterations: 2000,
      log: false,
    });
    perf.discreteMs = performance.now() - t;
  }

  const Ptrim = trimResult.principals || [];
  const deposit = trimResult.deposit || 0;
  const totalCost = trimResult.cost || 0;

  // ──────────────────────────────────────────────
  // Build investments and inflows from trimmed solution
  // ──────────────────────────────────────────────
  let investments;
  let inflowsAgg;

  {
    const t = performance.now();

    investments = [];
    const inflows = [];

    if (deposit > 0) {
      const cashInvId = "INV-DEP-1";
      investments.push({
        id: cashInvId,
        name: "Cash Deposit",
        type: "CASH",
        maturityDate: settleStr,
        firstInterestPaymentDate: null,
        ratePct: 0,
        rate: 0,
        price: 100,
        principal: deposit,
        cost: deposit,
        totalInterest: 0,
      });

      inflows.push({
        invId: cashInvId,
        date: settleStr,
        type: "Inflow",
        description: "Cash Deposit",
        inflow: deposit,
        outflow: 0,
        includesPrincipal: true,
      });
    }

    let invCounter = deposit > 0 ? 2 : 1;

    for (let k = 0; k < activeCandidates.length; k++) {
      const cand = activeCandidates[k];
      const P = Number(Ptrim[k] || 0);
      if (!P || P <= 0) continue;

      const invId = `INV-${invCounter++}`;

      investments.push({
        id: invId,
        name: cand.name,
        type: "SLGS",
        maturityDate: cand.maturity.format("YYYY-MM-DD"),
        firstInterestPaymentDate:
          cand.kind === "note"
            ? cand.firstInterestPaymentDate || null
            : null,
        ratePct: cand.ratePct,
        rate: cand.rate,
        price: 100,
        principal: P,
        cost: P,
        totalInterest: roundCents(cand.totalInterestPerDollar * P),
      });

      inflows.push(
        ...cand.inflowsPerDollar.map((cf) => ({
          invId,
          date: cf.date,
          type: "Inflow",
          description: cf.description,
          inflow: roundCents(cf.amount * P),
          outflow: 0,
          includesPrincipal: cf.includesPrincipal,
        }))
      );
    }

    inflowsAgg = aggregateInflows(inflows);
    perf.buildOutputMs = performance.now() - t;
  }

  const totalPrincipal = investments.reduce((s, r) => s + (r.principal || 0), 0);
  const slgsOnly = investments.filter((r) => r.type === "SLGS");
  const avgRate =
    slgsOnly.length > 0
      ? slgsOnly.reduce((s, r) => s + (r.ratePct || 0), 0) / slgsOnly.length
      : null;

  const summary = {
    totalPrincipal,
    totalCost,
    avgRate,
    count: investments.length,
    deposit,
  };

  // ──────────────────────────────────────────────
  // PERF summary
  // ──────────────────────────────────────────────
  perf.totalMs = performance.now() - t0All;
  perf.meta = {
    mode: "net",
    periods: periods?.length ?? 0,
    candidates: allCandidates?.length ?? 0,
    activeCandidates: activeCandidates?.length ?? 0,
    vars: lp?.objective?.vars?.length ?? 0,
    rows: lp?.subjectTo?.length ?? 0,
  };
  return { investments, inflows: inflowsAgg, summary };
}

/* ─────────────────────────────────────────────
   Candidate generation over full horizon
   (certificates & notes)
───────────────────────────────────────────── */
function buildCandidatesForHorizon(
  firstTargetDate,
  horizonDate,
  settleDate,
  _subDate,
  slgsBands
) {
  const out = [];
  const T0 = dayjs(firstTargetDate).startOf("day");
  const H = dayjs(horizonDate).startOf("day");
  if (!Array.isArray(slgsBands) || !slgsBands.length) return out;

  const settle = dayjs(settleDate).startOf("day");
  const settleStr = settle.format("YYYY-MM-DD");

  for (let M = T0.clone(); M.isSameOrBefore(H, "day"); M = M.add(1, "month")) {
    if (!M.isAfter(settle, "day")) continue;

    const mStr = M.format("YYYY-MM-DD");
    const kind = isSlgsCertificateMaturity(mStr, settleStr)
      ? "certificate"
      : "note";

    const band = findSlgsBandForMaturity(M, slgsBands);
    if (!band || !Number.isFinite(band.ratePct)) continue;

    const ratePct = Number(band.ratePct);
    const rate = ratePct / 100;

    if (kind === "certificate") {
      const dcf = dcfLogged(settleStr, mStr, "act/act");
      const interest = rate * dcf;

      out.push({
        ...band,
        kind,
        name: `SLGS Certificate ${band.tenor || ""}`.trim(),
        maturity: M.clone(),
        maturityStr: mStr,
        ratePct,
        rate,
        inflowsPerDollar: [
          {
            date: mStr,
            amount: 1 + interest,
            includesPrincipal: true,
            description: `SLGS Certificate maturity — ${band.name}`,
          },
        ],
        totalInterestPerDollar: interest,
      });
    } else {
      const noteName = `SLGS Note ${band.tenor || ""}`.trim();

      const { regular, firstInterestPaymentDate } = buildSlgsNoteCashflows({
        settlement: settleStr,
        maturity: mStr,
        principal: 1,
        ratePct,
        descriptionBase: noteName,
      });

      const inflows = regular.map((cf) => ({
        date: cf.date,
        amount: cf.amount,
        includesPrincipal: cf.includesPrincipal,
        description: cf.desc,
      }));

      const totalInt = inflows.reduce((s, cf) => s + cf.amount, 0) - 1;

      out.push({
        ...band,
        kind,
        name: noteName,
        maturity: M.clone(),
        maturityStr: mStr,
        ratePct,
        rate,
        inflowsPerDollar: inflows,
        totalInterestPerDollar: totalInt,
        firstInterestPaymentDate: firstInterestPaymentDate || null,
      });
    }
  }

  const byKey = new Map();
  for (const c of out) {
    const k = c.maturityStr || c.maturity.format("YYYY-MM-DD");
    const prev = byKey.get(k);
    if (!prev || c.ratePct > prev.ratePct) byKey.set(k, c);
  }

  return Array.from(byKey.values()).sort((a, b) => a.maturity - b.maturity);
}

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function roundCents(v) {
  return Math.round((Number(v) || 0) * 100) / 100;
}

function summarizeModel(_lp) {
  // No-op in production: intentionally no logging
}

function ensureBoundTypes(lp) {
  for (const row of lp.subjectTo) {
    row.bnds = row.bnds || { lb: 0, ub: 1e50 };
  }
  for (const b of lp.bounds) {
    if (b.lb == null) b.lb = 0;
    if (b.ub == null) b.ub = 1e50;
  }
}

function finalizeModel(lp) {
  const obj = new Set(lp.objective.vars.map((v) => v.name));
  const bnd = new Set(lp.bounds.map((v) => v.name));

  for (const r of lp.subjectTo) {
    for (const v of r.vars || []) {
      if (!obj.has(v.name)) {
        lp.objective.vars.push({ name: v.name, coef: 0 });
        obj.add(v.name);
      }

      if (!bnd.has(v.name)) {
        lp.bounds.push({ name: v.name, lb: 0, ub: 1e50 });
        bnd.add(v.name);
      }
    }
  }
}

function aggregateInflows(arr) {
  const key = (x) => `${x.date}||${x.description}`;
  const map = new Map();

  for (const cf of arr) {
    const k = key(cf);
    const p = map.get(k);
    if (!p) map.set(k, { ...cf });
    else p.inflow = roundCents(p.inflow + cf.inflow);
  }

  return Array.from(map.values()).sort((a, b) => dayjs(a.date) - dayjs(b.date));
}

function emptyResult() {
  return {
    investments: [],
    inflows: [],
    summary: {
      totalPrincipal: 0,
      totalCost: 0,
      count: 0,
      deposit: 0,
    },
  };
}
